import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ARConsole, ARGCSDownloadInformation, AROrderItem, ARResult } from '../models/agrorendement-common.model';
import { Campaign } from '../models/campaign.model';
import { Page } from '../models/page.model';
import { HttpWanaka } from '../utils/http.wanaka';
import { currentDateFormatted, isBooleanValue } from '../utils/utils';

import { saveAs } from 'file-saver';

export class AROrderFilter {
  campaignId: number | number[];
  organisationId: number | number[];
  farmId: number | number[];
}

export class ARResultFilter {
  prefixParcel: string;
  prefixExploitation: string;
  farm: number | string;
  campaign: number | number[];
  resultType: number | number[];
  organisation: number | number[];
  validationStatus: boolean;
  availabilityStatus: boolean;
  notificationStatus: boolean;
  notificationAdvisorsStatus: boolean;
  prefixIdParcel: number | string;
  advisorId: number | string;
}

export class ARCampaignNotificationFilter {
  campaign: number | number[];
  organisation: number | number[];
  sendToFarmers: boolean;
  sendToAdvisors: boolean;
}

export interface ARGenericToolService<C, O, OI> {
  getCampaigns(): Observable<C[]>;
  getPageCampaigns(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: string,
  ): Observable<Page<C>>;
  getOrderItems(
    orderItemIds: Array<number>,
    completionStatus: boolean,
    orderStatus: boolean,
    orderableStatus: boolean,
  ): Observable<OI[]>;
  getPageOrders(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: AROrderFilter,
  ): Observable<Page<O>>;
  getOrder(id: number): Observable<O>;
  saveOrder(order: O): Observable<O>;
  deleteOrder(order: O): Observable<any>;
  getAreaForOrders(filter: AROrderFilter, specy: string): Observable<OrderSummary>;
}

export function fillOrderFilter(filter: AROrderFilter) {
  let url = '';
  if (filter) {
    if (filter.farmId) {
      url += '&farm=' + filter.farmId;
    }
    if (filter.organisationId) {
      url += '&organisation=' + filter.organisationId;
    }
    if (filter.campaignId) {
      if (typeof filter.campaignId === 'number' || typeof filter.campaignId === 'string') {
        url += '&campaign=' + encodeURIComponent(String(filter.campaignId));
      } else {
        url += '&campaign=' + (<number[]>filter.campaignId).map(c => encodeURIComponent(String(c))).join(',');
      }
    }
  }
  return url;
}

export class OrderSummary {
  area: number;
  numberOfParcels: number;
}

@Injectable({ providedIn: 'root' })
export class ARConsoleService {
  private consoleUrl = '/tools/agrorendement/common/consoles';
  constructor(private http: HttpClient) {}

  getLast(farmId: number): Observable<ARConsole> {
    const url = this.consoleUrl + '/last?farm=' + farmId;
    return this.http.get<any>(url).pipe(
      map((response) => {
        return response.data;
      }),
      catchError(this.handleError),
    );
  }

  private handleError(error: any) {
    // FIXME
    console.error('Erreur lors de la récupération des listes', error);
    return throwError(() => error.message || error);
  }
}

// TODO fusionner avec ARGenericToolService

@Injectable()
export abstract class ARResultService<OI extends AROrderItem<any, any>, C extends Campaign> {
  // à redéfinir dans les sous-classes
  protected toolUrl: string;

  constructor(
    protected http: HttpClient,
    protected httpWanaka: HttpWanaka,
  ) {}

  protected abstract handleError(error: any): any;

  abstract getCampaigns(): Observable<C[]>;

  canPerfomLifecycleUpdates(): Observable<boolean> {
    // par défaut non
    // réimplémenter dans les sous-classes si diff
    return of(false);
  }

  getPageResults(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: ARResultFilter,
  ): Observable<Page<ARResult<OI>>> {
    let url = this.toolUrl + '/results?size=' + itemsPerPage + '&page=' + (pageIndex - 1);
    url += this.buildResultParamsFromFilter(filter);

    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    return <Observable<Page<ARResult<OI>>>>this.http.get<any>(url).pipe(
      map((response) => {
        const data: Page<ARResult<OI>> = response.data;
        for (const result of data.content) {
          this.handleResultDates(result);
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getResultIds(filter: ARResultFilter): Observable<any> {
    let url = this.toolUrl + '/results/all?onlyIds=true';
    url += this.buildResultParamsFromFilter(filter);
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data = response.data;
        return data.map((x: ARResult<OI>) => x.id);
      }),
      catchError(this.handleError),
    );
  }

  buildResultParamsFromFilter(filter: ARResultFilter) {
    let url = '';
    if (filter) {
      if (filter.prefixParcel) {
        url += '&prefixParcel=' + encodeURIComponent(filter.prefixParcel.trim());
      }
      if (filter.prefixExploitation) {
        url += '&prefixExploitation=' + encodeURIComponent(filter.prefixExploitation.trim());
      }
      if (filter.campaign) {
        url += '&campaign=' + encodeURIComponent(String(filter.campaign));
      }
      if (filter.farm) {
        url += '&farm=' + encodeURIComponent(String(filter.farm));
      }
      if (filter.resultType) {
        url += '&resultType=' + filter.resultType;
      }
      if (filter.organisation) {
        url += '&organisation=' + filter.organisation;
      }
      if (isBooleanValue(filter.availabilityStatus)) {
        url += '&availabilityStatus=' + filter.availabilityStatus;
      }
      if (isBooleanValue(filter.notificationStatus)) {
        url += '&notificationStatus=' + filter.notificationStatus;
      }
      if (isBooleanValue(filter.notificationAdvisorsStatus)) {
        url += '&notificationAdvisorsStatus=' + filter.notificationAdvisorsStatus;
      }
      if (isBooleanValue(filter.validationStatus)) {
        url += '&validationStatus=' + filter.validationStatus;
      }
      if (filter.advisorId) {
        url += '&advisor=' + encodeURIComponent(String(filter.advisorId));
      }
      if (filter.prefixIdParcel) {
        url += '&prefixIdParcel=' + encodeURIComponent(String(filter.prefixIdParcel));
      }
    }
    return url;
  }

  public saveResult(result: ARResult<OI>): Observable<ARResult<OI>> {
    // que PUT pour ARResult (la création n'est pas faite dans l'interface)
    const url = `${this.toolUrl}/results/${result.id}`;

    return <Observable<ARResult<OI>>>this.http.put(url, result).pipe(catchError(this.handleError));
  }

  public deleteResult(result: ARResult<OI>): Observable<any> {
    const url = `${this.toolUrl}/results/${result.id}`;

    return this.http.delete<any>(url).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  public downloadResult(result: ARResult<OI>): Observable<ARGCSDownloadInformation> {
    const url = `${this.toolUrl}/results/${result.id}/download`;
    return <Observable<ARGCSDownloadInformation>>this.http.get<any>(url).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  public downloadResultsBatch(resultIds: number[]) {
    const url = `${this.toolUrl}/results/download?resultIds=` + resultIds.join(',');
    const formattedDate = currentDateFormatted();
    const applicationType = 'application/zip';

    this.httpWanaka.blobQuery(url, applicationType).subscribe((x) => {
      saveAs(x, 'fichiers_' + formattedDate + '.zip');
    });
  }

  public validateBatch(ids: number[], isValidated = true): Observable<any> {
    const url = `${this.toolUrl}/results/validate`;

    return this.http.post<any>(url, { ids: ids, state: isValidated }).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  public expertValidateBatch(ids: number[], state = true): Observable<any> {
    const url = `${this.toolUrl}/results/expertValidate`;

    return this.http.post<any>(url, { ids: ids, state: state }).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  public makeAvailableBatch(ids: number[], isAvailable = true): Observable<any> {
    const url = `${this.toolUrl}/results/makeAvailable`;

    return this.http.post<any>(url, { ids: ids, state: isAvailable }).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  public invalidateNoficationBatch(ids: number[]): Observable<any> {
    const url = `${this.toolUrl}/results/invalidateNotification`;

    return this.http.post<any>(url, ids).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  public invalidateNoficationAdvisorsBatch(ids: number[]): Observable<any> {
    const url = `${this.toolUrl}/results/invalidateNotificationAdvisors`;

    return this.http.post<any>(url, ids).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  public notifyUsers(resultId: number, toSend: Array<boolean>): Observable<any> {
    return this.notifyUsersBatch([resultId], toSend);
  }

  public notifyUsersBatch(ids: number[], toSend: Array<boolean>): Observable<any> {
    const url = `${this.toolUrl}/results/notifications`;
    const dto: any = {};
    dto['resultIds'] = ids;
    dto['sendToFarmers'] = toSend[0];
    dto['sendToAdvisors'] = toSend[1];
    dto['sendToManagers'] = toSend[2];
    return this.http.post<any>(url, dto).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private handleResultDates(result: ARResult<any>): ARResult<any> {
    if (result.madeAvailableAt) {
      result.madeAvailableAt = new Date(<string>result.madeAvailableAt);
    }
    if (result.notifiedAt) {
      result.notifiedAt = new Date(<string>result.notifiedAt);
    }
    if (result.notifiedAdvisorsAt) {
      result.notifiedAdvisorsAt = new Date(<string>result.notifiedAdvisorsAt);
    }
    if (result.processedOn) {
      result.processedOn = new Date(<string>result.processedOn);
    }
    if (result.validatedAt) {
      result.validatedAt = new Date(<string>result.validatedAt);
    }
    result.createdAt = new Date(<string>result.createdAt);
    result.updatedAt = new Date(<string>result.updatedAt);

    return result;
  }
}
